import { KHPoint } from '../../../../../LearningWorlds/pixi-puzzle-framework/PuzzleEngine/KHPoint';
import { ManipFrame } from '../../../../../utils/utils';
import { ScorableDragRegion } from './ScorableDragRegion';

type RowLayoutData = {
  draggables: ScorableDragRegion[];
  totalWidth: number;
  totalHeight: number;
  yPos: number;
}

type DraggableLayoutData = {
  draggable: ScorableDragRegion;
  xPos: number;
  yPos: number;
}

/**
 * While this is a "group" it isn't an actual DIV element or anything - it just positions the draggables with some logic.
 */
export class ScorableDragGroup {
  private draggables: ScorableDragRegion[];
  private framePosition: ManipFrame;
  private draggablePadding = 15;
  private maxWidth;
  private topCenterPosition: KHPoint;
  private draggableRows: RowLayoutData[] = [];
  private scaleFactor: number;

  public constructor(parent: HTMLElement, topCenterPosition: KHPoint, maxWidth: number, draggables: ScorableDragRegion[], scaleFactor = 1) {
    this.draggables = draggables;
    this.maxWidth = maxWidth;
    this.topCenterPosition = topCenterPosition;
    this.scaleFactor = scaleFactor;

    this.hookupResetCallbacks();
    this.positionDraggables();
  }

  public getFrame(): ManipFrame {
    return this.framePosition;
  }

  public positionDraggables(animate = false): void {
    const draggableLayoutData = this.calculateDraggableLayout();

    for (const data of draggableLayoutData) {
      data.draggable.updateAnchorPosition(data.xPos, data.yPos);
    }
  }

  public calculateDraggableLayout(): DraggableLayoutData[] {
    let curRowLayoutData: RowLayoutData = { draggables: [], totalWidth: 0, totalHeight: 0, yPos: this.draggablePadding };
    const newDraggableLayoutData: DraggableLayoutData[] = [];
    const draggablesWithoutFrame = this.draggables.filter(draggable => !draggable.isPositioned && !draggable.isInRegion());
    this.draggableRows = [];

    let newRow = true;
    for (let i = 0; i < draggablesWithoutFrame.length; i++) {
      const draggable = draggablesWithoutFrame[i];
      const rect = draggable.getUnscaledSize();

      let newWidth = curRowLayoutData.totalWidth + rect.width + (newRow ? 0 : this.draggablePadding);
      newRow = false;

      if (newWidth > this.maxWidth) {
        this.draggableRows.push({...curRowLayoutData});
        curRowLayoutData = { draggables: [], totalWidth: 0, totalHeight: 0, yPos: curRowLayoutData.yPos + curRowLayoutData.totalHeight + this.draggablePadding };
        newWidth = rect.width + this.draggablePadding;
        newRow = true;
      }

      curRowLayoutData.draggables.push(draggable);
      curRowLayoutData.totalWidth = newWidth;
      curRowLayoutData.totalHeight = Math.max(curRowLayoutData.totalHeight, rect.height);
    }

    if (curRowLayoutData.draggables.length > 0) {
      this.draggableRows.push(curRowLayoutData);
    }

    let totalHeight = 0;

    for (let i = 0; i < this.draggableRows.length; i++) {
      const row = this.draggableRows[i];
      const xPos = (this.maxWidth - row.totalWidth) / 2;
      let currentX = xPos;
      let currentY = row.yPos + this.topCenterPosition.y;

      for (let j = 0; j < row.draggables.length; j++) {
        const draggable = row.draggables[j];
        const rect = draggable.getUnscaledSize();
        newDraggableLayoutData.push({ draggable, xPos: currentX, yPos: currentY });
        currentX += rect.width + this.draggablePadding;
      }

      totalHeight += row.totalHeight + this.draggablePadding;
    }

    this.framePosition = { x: 0, y: this.topCenterPosition.y + this.draggablePadding, width: this.maxWidth, height: totalHeight };

    return newDraggableLayoutData;
  }

  public recalculateAndAnimate(): void {
    const newLayout = this.calculateDraggableLayout();
    const shouldReturnToGroupDraggables = this.draggables.filter(draggable => draggable.config.returnunregistered);

    for (const draggable of shouldReturnToGroupDraggables) {
      const pos = newLayout.find(data => data.draggable === draggable);
      if (pos) {
        draggable.doResetTween({x: pos.xPos, y: pos.yPos});
      }
    }
  }

  private hookupResetCallbacks(): void {
    const shouldReturnToGroupDraggables = this.draggables.filter(draggable => draggable.config.returnunregistered);

    for (const draggable of shouldReturnToGroupDraggables) {
      draggable.onResetCallback = (): void => {
        this.recalculateAndAnimate();
      }
    }
  }
}
